import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "react-bootstrap";
import { UIInput } from "../../components/ui-input";
import { socialLogins } from "../../constant/sign-up.constant";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getItem, getLocalizedPathFromLocalStorage, setItem } from "../../helper/localstorage.helper";
import { signInWithEmailAndPassword, reset } from "../../features/signin/signin.slice";
import { AppDispatch, RootState } from "../../store";
import BankIdLogin from "../bankId-login/bankId-signin";
import BankIDSigninInline from '../../components/BankIDSigninInline/BankIDSigninInline';
import ReactivateModal from "../../components/reactivatemodal/reactivate.modal";
import { setToggleReactivate, setInactiveAccount } from "../../features/bankid/bankid.slice";
import { selectToggleReactivate, selectInactiveAccount, selectUserData as selectBankIDUserData } from "../../features/bankid/bankid.selectors";
import { handleSocialLoginAction, handleFormValidation, validateEmail, validatePassword } from "../../utils/registration-validation";
import { useSigninSuccess, useSigninError } from "../../hooks/useSignin";
import { RegistrationHeader } from "../../components/registeration-header-.component";
import useIsWideScreen from "../../hooks/useIsWideScreen";
import useToastRelay from "../../hooks/useToastRelay";
import { MailFieldIcon, LockFieldIcon, CtaArrowIcon } from "../../components/auth-field-icons";
import { SignupGuide } from "../../components/signup-guide/signup-guide.component";
import { AuthHeroTitle } from "../../components/auth-hero-title.component";
import "../../scss/registration/registration.scss";
import "../../scss/registration/auth-elevate.scss";
import { toast } from "react-toastify";
import { bankidcolor } from '../../components/images/svg/icons';

export const SignIn: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { loading: isSubmitting, error: submitError, isSuccess, user } = useSelector((state: RootState) => state.signin);
    const toggleReactivate = useSelector(selectToggleReactivate);
    const inactiveAccount = useSelector(selectInactiveAccount);
    const bankidUserData = useSelector(selectBankIDUserData);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    const [show, setShow] = useState(false);
    const [searchParams] = useSearchParams();
    const [visibility, setVisibility] = useState(false);
    const [loginMode, setLoginMode] = useState<'bankid-active' | 'email'>('bankid-active');
    /* Field the user is in — drives what the guide character says. */
    const [activeField, setActiveField] = useState<string | null>(null);
    /* Why the last submit was refused — the guide reads this out. */
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    /* Something the character should announce (success or failure). */
    const [announcement, setAnnouncement] = useState<{ message: string; kind: 'error' | 'success' } | null>(null);
    /* On phones the character is small, so those keep the toast. */
    const isWideScreen = useIsWideScreen();

    /* An announcement holds the cloud, so it needs a way out. The short grace
       period stops a message being wiped by the same render that produced it —
       a BankID error often arrives together with the view changing under it. */
    const announcedAt = useRef(0);

    const announce = useCallback((message: string, kind: 'error' | 'success') => {
        announcedAt.current = Date.now();
        setAnnouncement({ message, kind });
    }, []);

    const dismissAnnouncement = useCallback(() => {
        if (Date.now() - announcedAt.current < 600) return;
        setAnnouncement(null);
        setBlockedReason(null);
    }, []);


    /* Toasts raised from shared BankID code (its slice, component and hook)
       are relayed to the character too — those files stay untouched so the
       invoice and ID-verification flows keep their toasts. */
    const relayToGuide = useCallback((message: string, kind: 'error' | 'success') => {
        announce(message, kind);
    }, [announce]);

    useToastRelay(isWideScreen, relayToGuide);


    useSigninError(isWideScreen ? null : submitError);
    useSigninSuccess(isWideScreen ? false : isSuccess, user);

    /* wide screens: the character delivers what those hooks would have toasted */
    useEffect(() => {
        if (!isWideScreen || !submitError) return;
        announce(t(submitError) as string, 'error');
    }, [isWideScreen, submitError, t, announce]);

    useEffect(() => {
        if (!isWideScreen || !isSuccess || !user) return;
        announce(t("Logged in successfully") as string, 'success');
    }, [isWideScreen, isSuccess, user, t, announce]);

    useEffect(() => {
        dispatch(reset());
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);

    useEffect(() => {

        const message = searchParams.get("message");
        if (message) {
            const splitMessage = message.split("?")[0];

            if (splitMessage === "account_deactivated") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(false));
                return;
            }

            if (splitMessage === "inactive_account") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(true));
                return;
            }

            if (!bankidUserData?.token) return;
            if (isWideScreen) announce(t(message) as string, 'success');
            else toast.success(t(message) as string);
            const lang = bankidUserData?.userData?.preferredLanguage || searchParams.get('lang') || 'en-us';
            const preferredVersion = bankidUserData?.userData?.preferredPlatformVersion;
            setItem(
                'currentappversion',
                preferredVersion === 1 ? 'v1' : preferredVersion === 2 ? 'v2' : 'v2'
            );

            navigate(
                preferredVersion === 2
                    ? `/v2/dashboard?lang=${lang}`
                    : lang === 'en-us'
                        ? `/dashboard/activities?lang=${lang}`
                        : `/instrumentbrada/aktiviteter?lang=${lang}`
            );
        }

        setShow(false);
    }, [searchParams, t, dispatch, navigate]);

    useEffect(() => {
        if (toggleReactivate) {
            setLoginMode('bankid-active');
        }
    }, [toggleReactivate]);

    const handleInputChange = (field: 'email' | 'password', value: string) => {
        setBlockedReason(null);
        setAnnouncement(null);
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        const newErrors = { ...errors };

        if (field === 'email') {
            if (!value.trim()) {
                newErrors.email = t("EMAIL_REQUIRED") as string;
            } else if (!validateEmail(value)) {
                newErrors.email = t("EMAIL_INVALID") as string;
            } else {
                newErrors.email = '';
            }
        }

        if (field === 'password') {
            if (!value) {
                newErrors.password = t("PASSWORD_REQUIRED") as string;
            } else {
                newErrors.password = '';
            }
        }

        setErrors(newErrors);
    };

    const handleSocialLogin = (key: string) => {
        handleSocialLoginAction(key, (show: boolean) => { if (show) setLoginMode('bankid-active'); });
    };

    const handleReactivateClose = () => {
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    };

    const handleReactivateSuccess = () => {
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    };
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!handleFormValidation(formData, t, setErrors)) {
            /* say the first problem out loud through the character */
            setBlockedReason(
                (!formData.email.trim() && (t("EMAIL_REQUIRED") as string)) ||
                (!validateEmail(formData.email) && (t("EMAIL_INVALID") as string)) ||
                (t("PASSWORD_REQUIRED") as string)
            );
            return;
        }

        setBlockedReason(null);

        setLoading(true);
        try {
            const { payload } = await dispatch(signInWithEmailAndPassword({
                email: formData.email,
                password: formData.password,
            }));
            if (payload === "account_deactivated") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(false));
                return;
            }

            if (payload === "inactive_account") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(true));
                return;
            }
            // @ts-ignore
            if (!payload?.token) return;
            // @ts-ignore
            const lang = payload.userData?.preferredLanguage || searchParams.get('lang') || 'en-us';
            // @ts-ignore
            const preferredVersion = payload.userData?.preferredPlatformVersion;
            setItem(
                'currentappversion',
                preferredVersion === 1 ? 'v1' : preferredVersion === 2 ? 'v2' : 'v2'
            );

            navigate(
                preferredVersion === 2
                    ? `/v2/dashboard?lang=${lang}`
                    : lang === 'en-us'
                        ? `/dashboard/activities?lang=${lang}`
                        : `/instrumentbrada/aktiviteter?lang=${lang}`
            );

            return payload;
        } catch (error) {
            console.error("Sign in error:", error);
        } finally {
            setLoading(false);
        }
    };


    /* The BankID option buttons come from the shared BankIDComponent, so the
       guide listens on a wrapper rather than that component being modified. */
    const bankidHoverProps = {
        onMouseOver: (e: React.MouseEvent) => {
            const option = (e.target as HTMLElement).closest?.('.bankid-option');
            if (!option) {
                setActiveField(null);
                return;
            }
            const index = Array.from(option.parentElement?.children || []).indexOf(option);
            setActiveField(index === 0 ? 'bankidDevice' : 'bankidQr');
        },
        onMouseLeave: () => setActiveField(null),
    };


    /* Which BankID stage is on screen. Read from the DOM the shared component
       renders (`.bankid-options` → choosing, `.bankid-inline-qr` → QR shown),
       so that component keeps working untouched for the other flows. */
    const bankidWrapRef = useRef<HTMLDivElement | null>(null);
    const [bankidStage, setBankidStage] = useState<'qr' | 'waiting' | null>(null);

    useEffect(() => {
        const el = bankidWrapRef.current;
        if (!el) {
            setBankidStage(null);
            return;
        }

        const update = () => {
            if (el.querySelector('.bankid-inline-qr, .bankid-qr-container')) setBankidStage('qr');
            else if (!el.querySelector('.bankid-options')) setBankidStage('waiting');
            else setBankidStage(null);
        };

        update();
        const observer = new MutationObserver(update);
        observer.observe(el, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [loginMode]);


    /* anything the guide can talk about (a hovered link, a focused field) or
       leaving the BankID stage dismisses whatever was announced */
    useEffect(() => {
        if (activeField) dismissAnnouncement();
    }, [activeField, dismissAnnouncement]);

    useEffect(() => {
        dismissAnnouncement();
    }, [bankidStage, dismissAnnouncement]);

    const activeFieldError = activeField === 'email' ? errors.email : activeField === 'password' ? errors.password : '';

    const guideContext = {
        variant: 'signin' as const,
        activeField: bankidStage === 'qr'
            ? 'bankidQrOpen'
            : bankidStage === 'waiting'
                ? 'bankidWaiting'
                : activeField,
        alert: announcement
            ? { message: announcement.message, kind: announcement.kind, at: announcedAt.current }
            : blockedReason
            ? { message: blockedReason, blocking: true }
            : activeFieldError
                ? { message: activeFieldError }
                : null,
        values: { email: formData.email, password: formData.password },
        valid: {
            email: !!formData.email.trim() && validateEmail(formData.email.trim()) && !errors.email,
        },
    };

    return (
        <div className="registration-page-wrapper auth-elevate px-0 ">
            <div className="auth-blobs" aria-hidden="true">
                <span className="auth-blob auth-blob--one" />
                <span className="auth-blob auth-blob--two" />
            </div>
            <RegistrationHeader />
            <Container>
                <Row className="px-0 align-items-stretch">
                    {/* The hero column is a desktop affordance: below 992px the
                        card takes the whole screen and every message the guide
                        would carry goes to a toast instead (see useToastRelay /
                        useSigninError, both keyed on the same breakpoint). Not
                        rendering it there also stops the character's typewriter
                        and pointer listeners from running on phones. */}
                    {isWideScreen && (
                    <Col xs={12} lg={5} className="px-0 auth-hero-col">
                        <div className="auth-hero">
                            <AuthHeroTitle title={String(t("LOGIN_TITLE"))} />
                            <p className="auth-hero__subtitle">{t("LOGIN_SUBTITLE")}</p>
                            <SignupGuide context={guideContext} layout="panel" />
                        </div>
                    </Col>
                    )}

                    <Col xs={12} lg={7} className="px-0 auth-form-col">
                        <div className="auth-card">

                            {/* Content area */}
                            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

                            {/* ── BankID mode (default) ── */}
                            {loginMode === 'bankid-active' && (
                                <>
                                    <div ref={bankidWrapRef} {...bankidHoverProps}>
                                        {/* onSuccess is required to ungate the navigate block inside useBankID */}
                                        <BankIDSigninInline onSuccess={() => {}} />
                                    </div>

                                    <div className="registration-footer-text" style={{ marginTop: '16px' }}>
                                        <span>{t("REGISTRATION_DONT_HAVE_ACCOUNT")}</span>
                                        <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postregister', 'en-us': 'emailregister' })} className="sign-up-link-btn" onMouseEnter={() => setActiveField('signupLink')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('signupLink')} onBlur={() => setActiveField(null)}>{t("SIGN_UP_BUTTON")}</Link>
                                    </div>

                                    <div className="signin-or-divider">
                                        <span className="signin-or-line" />
                                        <span className="signin-or-text">or</span>
                                        <span className="signin-or-line" />
                                    </div>
                                    <p className="signin-bankid-hint">
                                        {t("SIGNIN_BANKID_HINT")}
                                    </p>
                                    <button
                                        type="button"
                                        className="signin-email-fallback-btn"
                                        onClick={() => setLoginMode('email')}
                                        onMouseEnter={() => setActiveField('switchEmail')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('switchEmail')} onBlur={() => setActiveField(null)}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{t("SIGN_IN_WITH_EMAIL")}</span>
                                    </button>
                                </>
                            )}

                            {/* ── Email & Password mode ── */}
                            {loginMode === 'email' && (
                            <div>

                                <form className="registration-form d-flex jutify-content-center" onSubmit={onSubmit}>

                                    {/* Email Field */}
                                    <UIInput
                                        type="email"
                                        label={t("REGISTRATION_EMAIL_LABEL")}
                                        placeholder={t("REGISTRATION_EMAIL_PLACEHOLDER")}
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        onFocus={() => setActiveField('email')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.email}
                                        errorMessage={errors.email}
                                        required={true}
                                        leadingIcon={<MailFieldIcon />}
                                        valid={!!formData.email.trim() && validateEmail(formData.email.trim()) && !errors.email}
                                    />

                                    {/* Password Field */}
                                    <UIInput
                                        type={visibility ? "text" : "password"}
                                        label={t("REGISTRATION_PASSWORD_LABEL")}
                                        placeholder={t("REGISTRATION_PASSWORD_PLACEHOLDER")}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        onFocus={() => setActiveField('password')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.password}
                                        errorMessage={errors.password}
                                        required={true}
                                        leadingIcon={<LockFieldIcon />}
                                        icon={
                                            <button
                                                className="btn btn-link p-0 m-0"
                                                type="button"
                                                aria-label={show ? t("HIDE_PASSWORD") : t("SHOW_PASSWORD")}
                                                title={show ? t("HIDE_PASSWORD") : t("SHOW_PASSWORD")}
                                                tabIndex={0}
                                                style={{ pointerEvents: 'auto', zIndex: 2 }}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setVisibility(s => !s);
                                                }}
                                            >
                                                {!visibility ? <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={18}
                                                    height={14}
                                                    viewBox="0 0 18 14"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1.42442 7.97582C1.13978 7.35655 1.13978 6.64214 1.42442 6.02288C2.742 3.15637 5.63866 1.16602 9.00019 1.16602C12.3617 1.16602 15.2584 3.15637 16.576 6.02288C16.8606 6.64215 16.8606 7.35656 16.576 7.97582C15.2584 10.8423 12.3617 12.8327 9.00019 12.8327C5.63866 12.8327 2.74201 10.8423 1.42442 7.97582Z"
                                                        stroke="black"
                                                        strokeWidth={2}
                                                    />
                                                    <path
                                                        d="M11.5002 6.99935C11.5002 8.38006 10.3809 9.49935 9.00019 9.49935C7.61948 9.49935 6.50019 8.38006 6.50019 6.99935C6.50019 5.61864 7.61948 4.49935 9.00019 4.49935C10.3809 4.49935 11.5002 5.61864 11.5002 6.99935Z"
                                                        stroke="black"
                                                        strokeWidth={2}
                                                    />
                                                </svg> : <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={22}
                                                    height={18}
                                                    viewBox="0 0 22 18"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1.9095 7.82824L2.81811 8.24589L2.81811 8.24588L1.9095 7.82824ZM20.0913 10.1718L19.1827 9.75412L20.0913 10.1718ZM1.9095 10.1718L1.00089 10.5894H1.00089L1.9095 10.1718ZM20.0913 7.82824L19.1827 8.24588V8.24588L20.0913 7.82824ZM16.9678 2.75789C16.5041 2.45792 15.885 2.59066 15.585 3.05438C15.285 3.5181 15.4178 4.13719 15.8815 4.43716L16.9678 2.75789ZM6.1616 13.59C5.69622 13.2926 5.07787 13.4288 4.78049 13.8942C4.48311 14.3596 4.6193 14.9779 5.08469 15.2753L6.1616 13.59ZM2.20093 12.6017C2.53267 13.0432 3.15955 13.1322 3.6011 12.8005C4.04265 12.4687 4.13166 11.8418 3.79992 11.4003L2.20093 12.6017ZM12.8015 3.18005C13.3427 3.28992 13.8706 2.94022 13.9804 2.39897C14.0903 1.85772 13.7406 1.32989 13.1994 1.22002L12.8015 3.18005ZM19.7075 1.70711C20.098 1.31658 20.098 0.683418 19.7075 0.292893C19.317 -0.0976311 18.6838 -0.0976311 18.2933 0.292893L19.7075 1.70711ZM2.2933 16.2929C1.90278 16.6834 1.90278 17.3166 2.2933 17.7071C2.68383 18.0976 3.31699 18.0976 3.70752 17.7071L2.2933 16.2929ZM11.0004 1C6.56168 1 2.73907 3.62905 1.00089 7.4106L2.81811 8.24588C4.24213 5.14781 7.3715 3 11.0004 3V1ZM11.0004 17C15.4392 17 19.2618 14.371 21 10.5894L19.1827 9.75412C17.7587 12.8522 14.6293 15 11.0004 15V17ZM1.00089 7.4106C0.537465 8.41881 0.537464 9.5812 1.00089 10.5894L2.81811 9.75412C2.59839 9.2761 2.59839 8.7239 2.81811 8.24589L1.00089 7.4106ZM19.1827 8.24588C19.4025 8.7239 19.4025 9.2761 19.1827 9.75412L21 10.5894C21.4634 9.58119 21.4634 8.41881 21 7.4106L19.1827 8.24588ZM21 7.4106C20.1226 5.50182 18.7158 3.88861 16.9678 2.75789L15.8815 4.43716C17.3133 5.36336 18.4653 6.68497 19.1827 8.24588L21 7.4106ZM5.08469 15.2753C6.79347 16.3672 8.82458 17 11.0004 17V15C9.21745 15 7.55838 14.4826 6.1616 13.59L5.08469 15.2753ZM1.00089 10.5894C1.32986 11.3051 1.73322 11.9791 2.20093 12.6017L3.79992 11.4003C3.41693 10.8905 3.08699 10.3391 2.81811 9.75412L1.00089 10.5894ZM13.1994 1.22002C12.488 1.07563 11.7525 1 11.0004 1V3C11.6182 3 12.2204 3.06209 12.8015 3.18005L13.1994 1.22002ZM8.17199 11.8284C9.73409 13.3905 12.2667 13.3905 13.8288 11.8284L12.4146 10.4142C11.6336 11.1953 10.3673 11.1953 9.58621 10.4142L8.17199 11.8284ZM13.8288 11.8284C15.3909 10.2663 15.3909 7.73367 13.8288 6.17157L12.4146 7.58579C13.1957 8.36684 13.1957 9.63317 12.4146 10.4142L13.8288 11.8284ZM18.2933 0.292893L2.2933 16.2929L3.70752 17.7071L19.7075 1.70711L18.2933 0.292893Z"
                                                        fill="black"
                                                    />
                                                </svg>
                                                }

                                            </button>
                                        }
                                    />


                                    <div className="flex-row d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex justify-content-center flex-row align-items-center">
                                            <div className="checkbox-wrapper" onMouseEnter={() => setActiveField('rememberMe')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('rememberMe')} onBlur={() => setActiveField(null)}>
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox-input"
                                                        checked={formData.rememberMe}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            rememberMe: e.target.checked
                                                        }))}
                                                    />
                                                    <span className="checkbox-checkmark"></span>
                                                    <span className="checkbox-text remember-me">{t("REMEMBER_ME")}</span>
                                                </label>
                                            </div>
                                        </div>

                                        <Link to={getLocalizedPathFromLocalStorage(
                                            { get: (key: string) => getItem(key) ?? null },
                                            { 'sv-se': 'glomt-ditt-losenord', 'en-us': 'forgot-password' }
                                        )} className="forgot-password-link" onMouseEnter={() => setActiveField('forgotPassword')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('forgotPassword')} onBlur={() => setActiveField(null)}>
                                            {t("FORGOT_PASSWORD")}
                                        </Link>
                                    </div>


                                    <button type="submit" className="submit-btn" disabled={loading || isSubmitting} onMouseEnter={() => setActiveField('submit')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('submit')} onBlur={() => setActiveField(null)}>
                                        <span>{(loading || isSubmitting) ? t("SIGN_IN_LOADING") : t("SIGN_IN_BUTTON")}</span>
                                        {!(loading || isSubmitting) && <CtaArrowIcon />}
                                    </button>

                                </form>

                                <div className="registration-footer-text">
                                    <span>{t("REGISTRATION_DONT_HAVE_ACCOUNT")}</span>
                                    <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postregister', 'en-us': 'emailregister' })} className="sign-up-link-btn" onMouseEnter={() => setActiveField('signupLink')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('signupLink')} onBlur={() => setActiveField(null)}>{t("SIGN_UP_BUTTON")}</Link>
                                </div>

                                <div className="signin-or-divider" style={{ marginTop: '8px' }}>
                                    <span className="signin-or-line" />
                                    <span className="signin-or-text">or</span>
                                    <span className="signin-or-line" />
                                </div>
                                <button
                                    type="button"
                                    className="signin-email-fallback-btn"
                                    onClick={() => setLoginMode('bankid-active')}
                                    onMouseEnter={() => setActiveField('switchBankid')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('switchBankid')} onBlur={() => setActiveField(null)}
                                >
                                    <img src={bankidcolor} alt="" width={15} height={15} style={{ objectFit: 'contain' }} />
                                    <span>{t("LOGIN_WITH_BANKID")}</span>
                                </button>
                            </div>
                            )}
                            </div>{/* end flex-1 content area */}
                        </div>

                    </Col>
                </Row>
            </Container>

            {/* BankID is now shown inline above — modal no longer needed here */}
            <ReactivateModal
                show={toggleReactivate}
                onClose={handleReactivateClose}
                onSuccess={handleReactivateSuccess}
                email={formData.email}
                getParamsEmail={searchParams.get("email") || undefined}
                inactiveAccount={inactiveAccount}
                mode="login"
            />
        </div>
    );
};

export default SignIn;
